import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, X, BookOpen, Users, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Skeleton } from '../components/ui/skeleton';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OmasBibliotheek = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [grandmas, setGrandmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchGrandmas();
  }, []);

  const fetchGrandmas = async () => {
    try {
      const response = await axios.get(`${API}/grandmas/with-counts`);
      setGrandmas(response.data);
    } catch (error) {
      console.error('Failed to fetch grandmas:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = grandmas.filter(g => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return g.name.toLowerCase().includes(q) || g.bio.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="omas-bibliotheek-page">
      <SEO path="/omas" />
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-old-rose/20 rounded-full mb-4">
              <Users className="w-4 h-4 text-old-rose" />
              <span className="text-sm font-accent text-old-rose font-medium">{t('omas.badge')}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-3">
              {t('omas.title')}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg font-accent max-w-lg mx-auto">
              {t('omas.subtitle')}
            </p>
          </div>

          {/* Search */}
          <div className="max-w-md mx-auto mb-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="omas-search"
                placeholder={t('omas.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-muted-foreground mt-2 text-center">
                {filtered.length} {filtered.length === 1 ? t('omas.result') : t('omas.results')}
                {' '}{t('library.for')} "{searchQuery}"
              </p>
            )}
          </div>

          {/* Grandma Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-card rounded-2xl p-6 border border-border">
                  <div className="flex items-center gap-4 mb-4">
                    <Skeleton className="w-16 h-16 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                  </div>
                  <Skeleton className="h-12 w-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-display text-2xl text-foreground mb-2">
                {searchQuery ? t('omas.no_results') : t('omas.empty')}
              </h3>
              <p className="text-muted-foreground font-accent">
                {searchQuery ? t('omas.try_other') : t('omas.coming_soon')}
              </p>
              {searchQuery && (
                <Button variant="outline" className="mt-4 rounded-full" onClick={() => setSearchQuery('')}>
                  {t('library.clear_filters')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((grandma, index) => (
                <motion.div
                  key={grandma.id}
                  data-testid={`oma-card-${grandma.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.4 }}
                  onClick={() => navigate(`/grandma/${grandma.id}`)}
                  className="group bg-card rounded-2xl p-6 border-2 border-border hover:border-gold/40 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <Avatar className="w-16 h-16 border-3 border-gold/20 shadow-md group-hover:border-gold/40 transition-colors">
                      <AvatarImage src={grandma.photo_url} alt={grandma.name} />
                      <AvatarFallback className="bg-old-rose/30 text-foreground text-2xl font-display">
                        {grandma.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-xl text-foreground truncate group-hover:text-gold transition-colors">
                        {grandma.name}
                      </h3>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <BookOpen className="w-3.5 h-3.5" />
                        <span className="font-accent">
                          {grandma.story_count} {grandma.story_count === 1 ? t('omas.story') : t('omas.stories')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground font-body line-clamp-2 mb-4 leading-relaxed">
                    {grandma.bio || t('omas.no_bio')}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground/60 font-accent">
                      {t('omas.view_stories')}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OmasBibliotheek;
